import Wizardable from 'components/widgets/Wizardable';
import WizardFooter from 'components/widgets/WizardFooter';
import PropTypes from 'prop-types';
import React from 'react';
import AutoComplete from '../../components/molecules/AutoComplete';
import AddFutureItems from '../containers/AddFutureItems';
import { translationPrefix } from '../utils';
import css from './AppSelection.css';
import PicturizedMultiSelect from './PicturizedMultiSelect';

class AppSelection extends Wizardable {
    constructor(props) {
        super(props);
        this.state = {
            appFilter: '',
        };
        this.selectAll = this.selectAll.bind(this);
        this.onSearchChanged = this.onSearchChanged.bind(this);
        this._textPredicate = this._textPredicate.bind(this);
        this.buttonOverrides = props.wizardOverrideButtons;
    }

    selectAll(value) {
        const { apps, onChange, enableAddFuture } = this.props;
        onChange(
            apps.map(app => app.id),
            value
        );
        enableAddFuture();
    }

    onItemSelected = (id, value) => {
        const { onChange } = this.props;
        onChange([id], value);
    };

    onSearchChanged(val) {
        this.setState({ appFilter: val });
    }

    _textPredicate(app) {
        const { appFilter } = this.state;
        return app.name.toLocaleLowerCase().includes(appFilter.toLocaleLowerCase().trim());
    }

    validate() {
        const { apps, translate } = this.props;
        const isValid = apps.filter(a => a.selected).length;

        const tooltipButtons = ['next'];
        this.setTooltip(
            tooltipButtons,
            isValid
                ? null
                : translate('STATIC.PAGES.TEAM_MEMBERS.SELECT_AT_LEAST_ONE_TOOLTIP', {
                      itemType: 'app',
                  })
        );

        return isValid;
    }

    render() {
        const { apps, lastAction, translate } = this.props;
        const { appFilter } = this.state;
        const selectedCount = apps.filter(app => app.selected).length;
        const totalCount = apps.length;
        const formattedApps = apps.map(app => ({
            ...app,
            ...(app.icon && {
                Icon: 'img',
                iconProps: { src: app.icon, alt: app.name },
            }),
        }));

        return (
            <React.Fragment>
                <this.AnimationItem initialPose={lastAction} pose="visible" className={css.page}>
                    <div>
                        <div className={css.headline}>
                            <div className={css.preamble}>
                                <div>Select apps data to be available for this user.</div>
                                <div className={css.faqLine}>
                                    For more information{' '}
                                    <a
                                        href="https://singular.zendesk.com/hc/en-us/articles/360020835952-Team-Management-FAQ"
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        read the FAQ
                                    </a>
                                    .
                                </div>
                            </div>
                            <div className={css.selectorContainer}>
                                <div className={css.counter}>
                                    {translate(`${translationPrefix}.SELECTED_COUNT`, {
                                        selectedCount,
                                        totalCount,
                                    })}
                                </div>
                            </div>
                        </div>
                        <AutoComplete
                            placeholder={translate(`${translationPrefix}.PLACEHOLDER.APP_SEARCH`)}
                            containerClass={css.autocompleteContainer}
                            selectOptions={{
                                options: [],
                                noOptionsMessage: () => null,
                                isSearchable: true,
                                inputValue: appFilter,
                            }}
                            onInputChange={this.onSearchChanged}
                            controlledValue={appFilter}
                        />
                    </div>
                    <PicturizedMultiSelect
                        className={css.multiSelectContainer}
                        allItems={formattedApps}
                        filteredItems={formattedApps.filter(this._textPredicate)}
                        onChange={this.onItemSelected}
                        selectAll={this.selectAll}
                    />
                </this.AnimationItem>
                <WizardFooter
                    buttons={this.getButtons()}
                    leftComponents={<AddFutureItems itemType="apps" form="app" dimensionFilter />}
                />
            </React.Fragment>
        );
    }
}

AppSelection.propTypes = {
    apps: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number.isRequired,
            name: PropTypes.string.isRequired,
            displayName: PropTypes.string.isRequired,
            icon: PropTypes.string.isRequired,
            selected: PropTypes.bool.isRequired,
            sites: PropTypes.arrayOf(
                PropTypes.shape({
                    id: PropTypes.number.isRequired,
                    publicId: PropTypes.string.isRequired,
                    platform: PropTypes.string.isRequired,
                    icon: PropTypes.string.isRequired,
                })
            ),
        })
    ),
    onChange: PropTypes.func,
    enableAddFuture: PropTypes.func.isRequired,
    wizardOverrideButtons: PropTypes.objectOf(PropTypes.any),
    translate: PropTypes.func,
};

AppSelection.defaultProps = {
    apps: [],
    onChange: () => {},
    wizardOverrideButtons: {},
    translate: x => x,
};

export default AppSelection;
